import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';

import BulkLotUpdateLotTable from './BulkLotUpdateLotTable';
import BulkLotUpdateNdcPicker, { BULK_LOT_UPDATE_CHOICE_SHAPE } from './BulkLotUpdateNdcPicker';
import BulkLotUpdatePreview from './BulkLotUpdatePreview';
import ChangeNdcLotLotSection from './ChangeNdcLotLotSection';
import ChangeNdcLotManualEntry from './ChangeNdcLotManualEntry';
import ChangeNdcLotManualSummary from './ChangeNdcLotManualSummary';
import useBulkLotUpdate from '../hooks/useBulkLotUpdate';
import { formatQty } from '../utils/bulkLotNdcStatus';
import { warningTextTone, warningTint } from '../utils/warningTone';

const formatRowCount = (count) => `${count} row${count === 1 ? '' : 's'}`;

/** "Change 3 rows to lot Lot7781, 30 as System Deficit": what the operator confirms. */
const describeConfirmation = ({ outcome, lot }) => {
  const deficit =
    outcome.deficitQty > 0 ? `${formatQty(outcome.deficitQty)} as ${SYSTEM_DEFICIT}` : '';
  const allDeficit = lot.isManual ? `all ${deficit}` : deficit;

  return `Change ${formatRowCount(outcome.changeCount)} to lot ${lot.lotNumber}${
    deficit ? `, ${allDeficit}` : ''
  }`;
};

/**
 * POM-6537 — "Bulk Lot Update" in Review Lot & NDC: one lot for every row of one NDC that can take
 * it, without opening a row first.
 *
 * The NDC first, typed and picked, because a lot belongs to one NDC. Then the lot: from stock,
 * across the NDC's facilities, or recorded by hand at one of them. Then "What will happen", every
 * row of the NDC grouped by what Apply would do to it, and a confirmation of that before Apply. It
 * issues no requests: everything comes from the review payload. Apply only stages, through the
 * review's planner (useBulkLotUpdate); the review's Commit sends it, and "Undo bulk change" takes
 * it back.
 *
 * Mounted only while open, so its state starts afresh every time.
 */
const BulkLotUpdateDialog = ({
  choices,
  initialChoiceKey,
  availabilityFor,
  recordableFor,
  batchLotContextFor,
  batchLotShareFor,
  planBulkApplyFor,
  lotLookupCache,
  todayStr,
  onApply,
  onCancel,
}) => {
  const bulk = useBulkLotUpdate({
    choices,
    initialChoiceKey,
    availabilityFor,
    recordableFor,
    batchLotContextFor,
    batchLotShareFor,
    planBulkApplyFor,
    lotLookupCache,
    todayStr,
  });
  const { choice, manualLotDraft, outcome, pickedLot } = bulk;
  const rowCount = bulk.bulkChanges.length;

  // Asked only where there is a choice of facility: a lot typed by hand is one vial in one place.
  const facilityField = choice?.facilities.length > 1 && bulk.manualFacility && (
    <TextField
      select
      size='small'
      label='Facility'
      value={bulk.manualFacility.fulfillingPharmacyId}
      onChange={(event) => bulk.selectManualFacility(event.target.value)}
      disabled={bulk.manualFacilities.length === 1}
      helperText='A lot typed by hand is one vial at one facility'
      FormHelperTextProps={{ sx: { color: warningTextTone } }}
      sx={{ flex: '1 1 100%', maxWidth: 360 }}
    >
      {bulk.manualFacilities.map((facility) => (
        <MenuItem key={facility.fulfillingPharmacyId} value={facility.fulfillingPharmacyId}>
          {`${facility.facilityName} · ${formatRowCount(facility.rowCount)}`}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    // Escape cancels; a backdrop click does not. Held at the top so a change of height only
    // grows it downwards.
    <Dialog
      open
      onClose={(event, reason) => reason !== 'backdropClick' && onCancel()}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { maxWidth: 760 } }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            Bulk Lot Update
            <Typography variant='body2' color='text.secondary' component='div' sx={{ mt: 0.5 }}>
              Pick an NDC and a lot. Every row of that NDC that can take it changes: check what will
              happen, confirm, and Apply. Nothing is saved until you commit.
            </Typography>
          </Box>
          <IconButton aria-label='Close' onClick={onCancel} size='small' sx={{ mt: -0.5 }}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <BulkLotUpdateNdcPicker
          choices={choices}
          selectedKey={choice?.key ?? null}
          onSelect={bulk.selectChoice}
        />

        {!choice && (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Pick an NDC to see its lots, and what a lot would do to its rows.
          </Typography>
        )}

        {choice && (
          <>
            <Divider sx={{ my: 2 }} />
            <ChangeNdcLotLotSection
              caption={
                manualLotDraft.isActive
                  ? 'entered manually'
                  : `for NDC ${choice.ndc} · earliest expiry first`
              }
              headerAction={
                bulk.manualFacilities.length > 0 &&
                !manualLotDraft.isActive && (
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<AddIcon />}
                    onClick={bulk.startManualEntry}
                    sx={{
                      color: warningTextTone,
                      borderColor: (theme) => warningTint(theme, 0.6),
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Enter lot manually
                  </Button>
                )
              }
            >
              {!manualLotDraft.isActive && (
                <BulkLotUpdateLotTable
                  lots={bulk.lots}
                  facilities={choice.facilities}
                  selectedLotKey={bulk.selectedLotKey}
                  onSelectLot={bulk.selectStockLot}
                />
              )}
              {manualLotDraft.isOpen && (
                <ChangeNdcLotManualEntry
                  derived={manualLotDraft.entry}
                  lotNumber={manualLotDraft.fields.lotNumber}
                  confirmLotNumber={manualLotDraft.fields.confirmLotNumber}
                  onLotNumberChange={manualLotDraft.changeLotNumber}
                  onConfirmLotNumberChange={manualLotDraft.setConfirmLotNumber}
                  onPickLot={manualLotDraft.pickLot}
                  onBudChange={manualLotDraft.setBud}
                  onAddLot={manualLotDraft.add}
                  onClose={manualLotDraft.dismiss}
                  isCheckingLotNumber={manualLotDraft.isChecking}
                  showsWholeFillQuantity={false}
                  facilityField={facilityField}
                />
              )}
              {!manualLotDraft.isOpen && manualLotDraft.selection && (
                <ChangeNdcLotManualSummary
                  selection={manualLotDraft.selection}
                  onChange={manualLotDraft.edit}
                  onRemove={manualLotDraft.remove}
                />
              )}
            </ChangeNdcLotLotSection>
          </>
        )}

        {/* The rows wait for a lot: until there is one, there is nothing to say about them. */}
        {outcome && (
          <>
            <Divider sx={{ my: 2 }} />
            <BulkLotUpdatePreview
              outcome={outcome}
              lot={pickedLot}
              scope={bulk.scope}
              hasScopeChoice={bulk.hasScopeChoice}
              onScopeChange={bulk.setScope}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {rowCount > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                size='small'
                checked={bulk.isConfirmed}
                onChange={(event) => bulk.setConfirmed(event.target.checked)}
              />
            }
            label={describeConfirmation({ outcome, lot: pickedLot })}
            sx={{ mr: 'auto', '& .MuiFormControlLabel-label': { typography: 'body2' } }}
          />
        )}
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!bulk.isConfirmed}
          onClick={() => onApply({ bulkChanges: bulk.bulkChanges })}
        >
          {rowCount > 0 ? `Apply to ${formatRowCount(rowCount)}` : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BulkLotUpdateDialog.propTypes = {
  /** buildBulkLotUpdateChoices' output, kept to the facilities with something to pick. */
  choices: PropTypes.arrayOf(BULK_LOT_UPDATE_CHOICE_SHAPE).isRequired,
  initialChoiceKey: PropTypes.string,
  /** Item -> its product and facility's availability entry, as the row picker gets it. */
  availabilityFor: PropTypes.func.isRequired,
  /** Item -> what may be recorded by hand for its product and facility. */
  recordableFor: PropTypes.func.isRequired,
  /** Item (or null) -> `{ sessionManualLots, ndcClaims }`, as batchLotContextForRow returns. */
  batchLotContextFor: PropTypes.func.isRequired,
  /** POM-6536 — item -> Map of lot id -> what the rest of the batch leaves on it. */
  batchLotShareFor: PropTypes.func.isRequired,
  /** The review dialog's planner; see bulkApplyPlan. */
  planBulkApplyFor: PropTypes.func.isRequired,
  /** Cross-NDC lookups already answered in this sitting. Owned by the review dialog. */
  // eslint-disable-next-line react/forbid-prop-types
  lotLookupCache: PropTypes.object,
  todayStr: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

BulkLotUpdateDialog.defaultProps = {
  initialChoiceKey: null,
  lotLookupCache: null,
};

export default BulkLotUpdateDialog;
